/**
 * Printing Configuration for Photobook PDF Export
 * 
 * These specifications are based on the printing company requirements.
 * All measurements include 3mm bleed.
 * 
 * PDF Requirements:
 * - Inside pages: Single pages (No Spreads / No Crop Marks)
 * - 3mm bleed is included in the PDF size
 * 
 * For JPEG (RGB) spreads:
 * - 3mm bleed included in the JPEG (RGB) size
 * - No crop marks
 */

import formatSizes from '~/constants/format-sizes';

// Conversion helpers
export const mmToPoints = (mm: number): number => mm * 2.834645669;
export const mmToPixels = (mm: number, dpi: number = 300): number => (mm / 25.4) * dpi;
export const pixelsToMm = (pixels: number, dpi: number = 150): number => (pixels / dpi) * 25.4;

// Helper to deduce key from format name.
// Layflat formats get a '_layflat' suffix so they don't collide with
// Standard (non-layflat) formats that share the same logical size.
const getFormatKey = (name: string): string => {
  const isLayflat = name.toLowerCase().includes('layflat') || name.toLowerCase().includes('lay-flat');
  const suffix = isLayflat ? '_layflat' : '';

  if (name.includes('Softcover')) return 'A4_Portrait_Softcover';
  if (name.includes('200x200') || name.includes('20x20')) return `20x20${suffix}`;
  if (name.includes('300x300') || name.includes('30x30')) return `30x30${suffix}`;
  if (name.includes('A4') && name.includes('Landscape')) return `A4_Landscape${suffix}`;
  if (name.includes('A4') && name.includes('Portrait')) return `A4_Portrait${suffix}`;
  if (name.includes('A5')) return `A5_Portrait${suffix}`;
  if (name.includes('A3')) return `A3_Landscape${suffix}`;
  return `20x20${suffix}`; // Default
};

/**
 * PDF page sizes (single pages with 3mm bleed included)
 * Format: width x height in mm
 */
export const PDF_PAGE_SIZES: Record<string, { width: number; height: number }> = {};

/**
 * JPEG spread sizes (with 3mm bleed included)
 * For inside pages saved as spreads (2 pages side by side)
 * Format: width x height in mm
 */
export const JPEG_SPREAD_SIZES: Record<string, { width: number; height: number }> = {};

/**
 * Personalised Covers sizes (including crease, spine, and bleed)
 * Hardcover artwork needs to be saved as JPEG (RGB) spread in a separate file
 * Format: width x height in mm
 */
export const HARDCOVER_SIZES: Record<string, { width: number; height: number }> = {};

/**
 * Softcover sizes (including crease, spine, and bleed)
 * Format: width x height in mm
 */
export const SOFTCOVER_SIZES: Record<string, { width: number; height: number }> = {};

/**
 * SKU to size mapping
 * Maps product SKUs to their corresponding size format
 */
export const SKU_SIZE_MAP: Record<string, string> = {};

// Populate configurations from the source of truth (format-sizes.ts)
formatSizes.forEach(format => {
  const key = getFormatKey(format.name);

  // Map SKUs
  format.sku?.forEach(sku => {
    SKU_SIZE_MAP[sku] = key;
  });

  // Populate Sizes
  if (format.printSpecs?.pdfPage) {
    PDF_PAGE_SIZES[key] = {
      width: format.printSpecs.pdfPage.width,
      height: format.printSpecs.pdfPage.height
    };
  }
  if (format.printSpecs?.jpegSpread) {
    JPEG_SPREAD_SIZES[key] = {
      width: format.printSpecs.jpegSpread.width,
      height: format.printSpecs.jpegSpread.height
    };
  }

  // Populate Cover Sizes
  // Based on previous confirmation: Lay-Flat items were mapping to SOFTCOVER_SIZES, others to HARDCOVER_SIZES in the legacy file.
  // We maintain this logic to ensure consistency, but essentially we are mapping available checks.
  if (format.name.toLowerCase().includes('layflat') || format.name.toLowerCase().includes('lay-flat')) {
    if (format.coverSize) {
      SOFTCOVER_SIZES[key] = { width: format.coverSize.width, height: format.coverSize.height };
    }
  } else {
    if (format.coverSize) {
      HARDCOVER_SIZES[key] = { width: format.coverSize.width, height: format.coverSize.height };
    }
  }
});

/**
 * Spine width for standard (non–lay-flat) personalized hardcovers, by inside spread count
 * (`scenes.length - 1`, one scene = one spread).
 * Provided by Photo2Print — actual printed spine widths in mm.
 *
 * Spreads 1–44 → 8mm
 * Spreads 45–68 → 10mm
 * Spreads 69–88 → 12mm
 * Spreads 89+ → 14mm
 */
export const getSpineWidthMm = (spreadCount: number): number => {
  if (spreadCount <= 44) return 8;
  if (spreadCount <= 68) return 10;
  if (spreadCount <= 88) return 12;
  return 14;
};

/**
 * Lay-flat spine widths by inner spread count (one timeline scene after the cover = one spread).
 */
export const getLayflatSpineWidthMm = (spreadCount: number): number => {
  if (spreadCount <= 10) return 10;
  if (spreadCount <= 12) return 12;
  if (spreadCount <= 15) return 14;
  if (spreadCount <= 20) return 17;
  if (spreadCount <= 25) return 20;
  if (spreadCount <= 30) return 23;
  return 23;
};

/**
 * Quality settings for different export types
 */
export const EXPORT_QUALITY = {
  // For print-ready PDFs — 0.92 is visually identical to 1.0 at 300 DPI
  // while producing significantly smaller files and faster encoding
  PDF: {
    imageQuality: 0.92,
    format: 'image/jpeg',
    dpi: 300
  },
  // For preview/web
  PREVIEW: {
    imageQuality: 0.7,
    format: 'image/jpeg',
    dpi: 72
  },
  // For cover JPEGs
  COVER: {
    imageQuality: 0.92,
    format: 'image/jpeg',
    dpi: 300
  }
};

/**
 * Get the PDF page size for a given SKU or size format
 */
export const getPdfPageSize = (skuOrFormat: string): { width: number; height: number } => {
  const format = SKU_SIZE_MAP[skuOrFormat] || skuOrFormat;
  return PDF_PAGE_SIZES[format] || PDF_PAGE_SIZES['20x20'];
};

/**
 * Get the JPEG spread size for a given SKU or size format
 */
export const getJpegSpreadSize = (skuOrFormat: string): { width: number; height: number } => {
  const format = SKU_SIZE_MAP[skuOrFormat] || skuOrFormat;
  return JPEG_SPREAD_SIZES[format] || JPEG_SPREAD_SIZES['20x20'];
};

/**
 * Get the cover size for a given SKU, cover type, and size format
 */
export const getCoverSize = (
  skuOrFormat: string,
  coverType: 'hardcover' | 'softcover' = 'hardcover'
): { width: number; height: number } => {
  const format = SKU_SIZE_MAP[skuOrFormat] || skuOrFormat;
  const sizes = coverType === 'hardcover' ? HARDCOVER_SIZES : SOFTCOVER_SIZES;
  return sizes[format] || sizes['20x20'];
};

/**
 * Calculate the proper output size in pixels for high-quality print
 */
export const calculatePrintPixels = (
  widthMm: number,
  heightMm: number,
  dpi: number = 300
): { width: number; height: number } => {
  return {
    width: Math.round(mmToPixels(widthMm, dpi)),
    height: Math.round(mmToPixels(heightMm, dpi))
  };
};

export default {
  PDF_PAGE_SIZES,
  JPEG_SPREAD_SIZES,
  HARDCOVER_SIZES,
  SOFTCOVER_SIZES,
  SKU_SIZE_MAP,
  EXPORT_QUALITY,
  mmToPoints,
  mmToPixels,
  pixelsToMm,
  getPdfPageSize,
  getJpegSpreadSize,
  getCoverSize,
  calculatePrintPixels,
  getSpineWidthMm,
  getLayflatSpineWidthMm
};
